import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';
// import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;

  connectedCallback() {
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.#updateSelectedValueDisplay(event.target);
    this.#updateVariantIdInput(event.target);

    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    try {
      this.fetchUpdatedSection(this.buildRequestUrl());
    } catch (error) {
      console.error(error);
    }

    this.updateUrl(event.target);
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    /**
     * On retire toute query string existante de l'URL produit (ex: ?variant=123),
     * sinon on obtiendrait "?variant=123?option_values=..." et Shopify ignorerait
     * le paramètre option_values : la page renvoyée resterait sur l'ancienne variante.
     */
    const productUrl = (this.dataset.productUrl ?? '').split('?')[0];
    const params = [`option_values=${this.selectedOptionsValues.join(',')}`];

    /**
     * On passe par l'API de rendu de section (section_id) quand c'est possible :
     * la réponse est plus légère et reflète de façon fiable les options sélectionnées,
     * contrairement au rendu de page complète qui peut être servi depuis le cache.
     *
     * Exception : dans une popup quick-add ou une carte produit, la section englobante
     * est celle de la grille (collection, produits mis en avant...), pas celle de la page
     * produit. Un rendu de cette section ne contiendrait pas le formulaire produit,
     * donc on garde le rendu de page complète dans ces contextes.
     */
    const isInsidePopupOrCard = this.closest('[data-ref="popup-content"], quick-add-component, product-card, dialog') != null;

    if (!isInsidePopupOrCard) {
      const sectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
      if (sectionId) {
        params.push(`section_id=${sectionId}`);
      }
    }

    return `${productUrl}?${params.join('&')}`;
  }

  // Récupère la section mise à jour depuis le serveur.
  fetchUpdatedSection(requestUrl) {
    // Annule la requête fetch précédente si elle est encore en cours
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Variant picker: la requête a échoué (${response.status}) — ${requestUrl}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newVariantPickerSource = this.#findMatchingVariantPicker(html);
        const textContent = newVariantPickerSource?.querySelector('script[type="application/json"]')?.textContent;

        if (!textContent) {
          console.warn('Variant picker: variant data not found in response', requestUrl);
          return;
        }

        const variant = JSON.parse(textContent);

        try {
          this.updateVariantPicker(html);
        } catch (error) {
          console.error(error);
        }

        this.dispatchEvent(
          new VariantUpdatedEvent(variant, this.#getSelectedOptionValueIdSafe() ?? '', {
            html,
            productId: this.dataset.productId ?? '',
          }),
        );
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log("Fetch interrompu par l'utilisateur");
        } else {
          console.error(error);
        }
      });
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    const newVariantPickerSource = this.#findMatchingVariantPicker(newHtml);

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    this.innerHTML = newVariantPickerSource.innerHTML;
  }

  #findMatchingVariantPicker(html) {
    const pickers = html.querySelectorAll(this.tagName.toLowerCase());

    if (pickers.length <= 1) {
      return pickers[0];
    }

    const productUrl = this.dataset.productUrl;
    for (const picker of pickers) {
      if (picker.dataset.productUrl === productUrl) {
        return picker;
      }
    }

    return pickers[0];
  }

  #updateSelectedValueDisplay(target) {
    const optionContainer = target.closest('.variant-picker__option, fieldset');
    const selectedValueElement = optionContainer?.querySelector('.variant-picker__option-selected-value');

    if (!selectedValueElement) return;

    if (target instanceof HTMLSelectElement) {
      selectedValueElement.textContent = target.value;
      return;
    }

    if (target instanceof HTMLInputElement) {
      selectedValueElement.textContent = target.value;
    }
  }

  #updateVariantIdInput(target) {
    const variantId = this.#getVariantIdFromTarget(target);
    if (!variantId) return;

    const form = this.closest('form');
    form?.querySelectorAll('input[name="id"]').forEach((input) => {
      input.value = variantId;
    });
  }

  #getVariantIdFromTarget(target) {
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      return target.dataset.variantId || null;
    }

    if (target instanceof HTMLSelectElement) {
      return target.options[target.selectedIndex]?.dataset.variantId || null;
    }

    return null;
  }

  #getSelectedOptionValueIdSafe() {
    try {
      return this.selectedOptionId;
    } catch (error) {
      return undefined;
    }
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target) {
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    // Prépare l'URL pour la mise à jour de l'historique du navigateur
    const url = new URL(window.location.href);

    let variantId;

    // Extrait l'ID de la variante selon le type d'élément (radio ou select)
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Met à jour l'URL dans l'historique du navigateur si elle a changé
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked, .variant-picker__option-values input[type="radio"]:checked',
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked, .variant-picker__option-values input[type="radio"]:checked',
      ),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
